import React, { useEffect, useState } from "react";
import integrationsService from "../../../services/integrationsService";
import SourceScopeEditor from "../SourceScopeEditor";
import LoadingSpinner from "../../common/LoadingSpinner";
import { formatInUserTimezone } from "../../../utils/timezone";

const AVAILABLE_SOURCES = ["Chameleon", "FavorDrop", "DFC", "None"];

function ApiKeysTab() {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [createdKey, setCreatedKey] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await integrationsService.listApiKeys();
      setKeys(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () =>
    setDialog({
      label: "",
      scopes: ["read", "write"],
      scopeMode: "all",
      scopeSources: [],
      sandbox: false,
    });

  const toggleScope = (scope) => {
    setDialog((d) => {
      const scopes = d.scopes.includes(scope)
        ? d.scopes.filter((s) => s !== scope)
        : [...d.scopes, scope];
      return { ...d, scopes };
    });
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const allowedLeadSources =
        dialog.scopeMode === "all"
          ? null
          : dialog.scopeMode === "none"
            ? []
            : dialog.scopeSources;

      const res = await integrationsService.createApiKey({
        label: dialog.label,
        scopes: dialog.scopes,
        allowedLeadSources,
        sandbox: dialog.sandbox,
      });
      setCreatedKey(res);
      setDialog(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to create key");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    try {
      await integrationsService.revokeApiKey(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to revoke key");
    }
  };

  const rotate = async (id) => {
    if (!window.confirm("Rotate this API key? The old key will stop working.")) return;
    try {
      const res = await integrationsService.rotateApiKey(id);
      setCreatedKey(res);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to rotate key");
    }
  };

  if (loading) return <LoadingSpinner message="Loading API keys..." />;

  return (
    <div>
      <div className="management-header">
        <div>
          <h2>API Keys</h2>
          <p className="text-muted">
            Credentials for external systems to push and pull leads.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <i className="fas fa-plus" /> Create API Key
        </button>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {keys.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-key" />
          <h3>No API keys yet</h3>
          <p>Create a key to allow an external system to sync leads.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Prefix</th>
                <th>Scopes</th>
                <th>Source Scope</th>
                <th>Last Used</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td><strong>{k.label}</strong></td>
                  <td><code>ivsg_key_{k.key_prefix}…</code></td>
                  <td>{(k.scopes || []).join(", ")}</td>
                  <td>
                    {k.allowed_lead_sources === null ||
                    k.allowed_lead_sources === undefined
                      ? "All"
                      : Array.isArray(k.allowed_lead_sources) &&
                          k.allowed_lead_sources.length === 0
                        ? "None"
                        : k.allowed_lead_sources.join(", ")}
                  </td>
                  <td>
                    {k.last_used_at
                      ? formatInUserTimezone(k.last_used_at)
                      : "Never"}
                  </td>
                  <td>
                    {k.revoked_at ? (
                      <span className="status-badge inactive">Revoked</span>
                    ) : (
                      <span className="status-badge active">Active</span>
                    )}
                  </td>
                  <td className="actions">
                    {!k.revoked_at && (
                      <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => rotate(k.id)}
                        >
                          <i className="fas fa-sync-alt" /> Rotate
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => revoke(k.id)}
                        >
                          <i className="fas fa-ban" /> Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <div className="modal-overlay" onMouseDown={() => !saving && setDialog(null)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2>Create API Key</h2>

            <div className="form-group">
              <label>Label *</label>
              <input
                className="form-control"
                value={dialog.label}
                onChange={(e) => setDialog({ ...dialog, label: e.target.value })}
                placeholder="e.g. Partner XYZ — DFC Integration"
              />
            </div>

            <div className="form-group">
              <label>Scopes</label>
              <div className="scope-checkbox-row">
                {["read", "write", "file"].map((s) => (
                  <label key={s} className="source-scope-item">
                    <input
                      type="checkbox"
                      checked={dialog.scopes.includes(s)}
                      onChange={() => toggleScope(s)}
                    />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Source Scope</label>
              <SourceScopeEditor
                mode={dialog.scopeMode}
                sources={dialog.scopeSources}
                available={AVAILABLE_SOURCES}
                onChange={({ mode, sources }) =>
                  setDialog({ ...dialog, scopeMode: mode, scopeSources: sources })
                }
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label className="source-scope-item">
                <input
                  type="checkbox"
                  checked={dialog.sandbox}
                  onChange={(e) =>
                    setDialog({ ...dialog, sandbox: e.target.checked })
                  }
                />
                <span>Sandbox mode (stricter rate limits; data marked sandbox)</span>
              </label>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setDialog(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={saving || !dialog.label.trim() || !dialog.scopes.length}
              >
                {saving ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {createdKey && (
        <div className="modal-overlay">
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2>API Key Created</h2>
            <div className="source-scope-note">
              <i className="fas fa-exclamation-triangle" />
              <span>
                This is the <strong>only time</strong> this key will be shown.
                Copy it now and store it securely.
              </span>
            </div>

            <div className="form-group">
              <label>API Key</label>
              <textarea
                className="form-control"
                rows="3"
                readOnly
                value={createdKey.key}
                onFocus={(e) => e.target.select()}
              />
            </div>

            <div className="form-group">
              <label>Prefix</label>
              <input className="form-control" readOnly value={createdKey.prefix} />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  navigator.clipboard.writeText(createdKey.key);
                }}
              >
                <i className="fas fa-copy" /> Copy Key
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setCreatedKey(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiKeysTab;